import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const EPrescribing = ({ patient }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [prescriptions, setPrescriptions] = useState([]);

  const dentalMedications = [
    {
      id: 1,
      name: 'Amoxicillin',
      genericName: 'Amoxicillin',
      strength: '500mg',
      form: 'Capsule',
      category: 'Antibiotic',
      commonDosing: 'Take 1 capsule 3 times daily for 7 days',
      indications: 'Dental infections, prophylaxis'
    },
    {
      id: 2,
      name: 'Ibuprofen',
      genericName: 'Ibuprofen',
      strength: '600mg',
      form: 'Tablet',
      category: 'Pain Relief',
      commonDosing: 'Take 1 tablet every 6 hours as needed for pain',
      indications: 'Post-operative pain, inflammation'
    },
    {
      id: 3,
      name: 'Hydrocodone/Acetaminophen',
      genericName: 'Hydrocodone-Acetaminophen',
      strength: '5-325mg',
      form: 'Tablet',
      category: 'Pain Relief (Controlled)',
      commonDosing: 'Take 1 tablet every 4-6 hours as needed for severe pain',
      indications: 'Severe post-operative pain'
    },
    {
      id: 4,
      name: 'Chlorhexidine Gluconate',
      genericName: 'Chlorhexidine',
      strength: '0.12%',
      form: 'Oral Rinse',
      category: 'Antiseptic',
      commonDosing: 'Rinse with 15ml twice daily for 2 weeks',
      indications: 'Gingivitis, periodontal disease'
    },
    {
      id: 5,
      name: 'Clindamycin',
      genericName: 'Clindamycin HCl',
      strength: '300mg',
      form: 'Capsule',
      category: 'Antibiotic',
      commonDosing: 'Take 1 capsule 4 times daily for 7 days',
      indications: 'Dental infections (penicillin allergy)'
    }
  ];

  const [prescriptionForm, setPrescriptionForm] = useState({
    medication: '',
    dosage: '',
    quantity: '',
    refills: '0',
    instructions: '',
    pharmacy: ''
  });

  const pharmacies = [
    { value: 'cvs-main', label: 'CVS Pharmacy - Main Street' },
    { value: 'walgreens-oak', label: 'Walgreens - Oak Avenue' },
    { value: 'walmart', label: 'Walmart Pharmacy' },
    { value: 'rite-aid', label: 'Rite Aid Pharmacy' }
  ];

  const refillOptions = [
    { value: '0', label: 'No Refills' },
    { value: '1', label: '1 Refill' },
    { value: '2', label: '2 Refills' },
    { value: '3', label: '3 Refills' }
  ];

  const handleMedicationSelect = (medication) => {
    setSelectedMedication(medication);
    setPrescriptionForm({
      ...prescriptionForm,
      medication: medication?.name,
      dosage: medication?.strength,
      instructions: medication?.commonDosing
    });
  };

  const handleSendPrescription = () => {
    if (prescriptionForm?.medication && prescriptionForm?.pharmacy) {
      const newPrescription = {
        id: prescriptions?.length + 1,
        ...prescriptionForm,
        patient: patient?.name,
        date: new Date()?.toISOString()?.split('T')?.[0],
        status: 'sent'
      };
      setPrescriptions([newPrescription, ...prescriptions]);
      setPrescriptionForm({
        medication: '',
        dosage: '',
        quantity: '',
        refills: '0',
        instructions: '',
        pharmacy: ''
      });
      setSelectedMedication(null);
      alert('Prescription sent successfully!');
    }
  };

  const filteredMedications = searchQuery
    ? dentalMedications?.filter(med => 
        med?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        med?.genericName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        med?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      )
    : dentalMedications;

  return (
    <div className="space-y-6">
      {/* Patient Alert */}
      <div className="bg-warning/5 rounded-lg border border-warning/20 p-4">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-1" />
          <div>
            <h4 className="font-semibold text-foreground mb-1">Patient Allergies</h4>
            <p className="text-sm text-muted-foreground">Penicillin (Anaphylaxis), Sulfa drugs</p>
          </div>
        </div>
      </div>

      {/* Medication Search */}
      <div className="bg-card rounded-xl border border-border p-6">
        <h2 className="text-xl font-semibold text-foreground mb-4">Medication Formulary</h2>
        
        <Input
          type="text"
          placeholder="Search medications..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e?.target?.value)}
          className="mb-4"
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
          {filteredMedications?.map((medication) => (
            <button
              key={medication?.id}
              onClick={() => handleMedicationSelect(medication)}
              className={`text-left p-4 rounded-lg border transition-all ${
                selectedMedication?.id === medication?.id
                  ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50 bg-background'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-foreground">{medication?.name}</h3>
                <span className="text-xs px-2 py-1 rounded bg-muted text-muted-foreground">
                  {medication?.category}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-1">{medication?.strength} {medication?.form}</p>
              <p className="text-xs text-muted-foreground">{medication?.indications}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Prescription Form */}
      {selectedMedication && (
        <div className="bg-card rounded-xl border border-border p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Write Prescription</h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Medication"
                type="text"
                value={prescriptionForm?.medication}
                onChange={(e) => setPrescriptionForm({ ...prescriptionForm, medication: e?.target?.value })}
                disabled
              />
              <Input
                label="Dosage"
                type="text"
                value={prescriptionForm?.dosage}
                onChange={(e) => setPrescriptionForm({ ...prescriptionForm, dosage: e?.target?.value })}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Quantity"
                type="number"
                value={prescriptionForm?.quantity}
                onChange={(e) => setPrescriptionForm({ ...prescriptionForm, quantity: e?.target?.value })}
                placeholder="Enter quantity"
              />
              <Select
                label="Refills"
                options={refillOptions}
                value={prescriptionForm?.refills}
                onChange={(value) => setPrescriptionForm({ ...prescriptionForm, refills: value })}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Instructions</label>
              <textarea
                value={prescriptionForm?.instructions}
                onChange={(e) => setPrescriptionForm({ ...prescriptionForm, instructions: e?.target?.value })}
                placeholder="Patient instructions..."
                className="w-full min-h-[100px] px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
            </div>
            
            <Select
              label="Pharmacy"
              options={pharmacies}
              value={prescriptionForm?.pharmacy}
              onChange={(value) => setPrescriptionForm({ ...prescriptionForm, pharmacy: value })}
              placeholder="Select pharmacy"
              required
            />
            
            <div className="flex items-center gap-3">
              <Button 
                variant="default" 
                iconName="Send" 
                iconPosition="left"
                onClick={handleSendPrescription}
                disabled={!prescriptionForm?.medication || !prescriptionForm?.pharmacy}
              >
                Send to Pharmacy
              </Button>
              <Button variant="outline" iconName="Printer">
                Print Prescription
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Prescription History */}
      {prescriptions?.length > 0 && (
        <div className="bg-card rounded-xl border border-border p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Recent Prescriptions</h2>
          <div className="space-y-3">
            {prescriptions?.map((rx) => (
              <div key={rx?.id} className="bg-background rounded-lg border border-border p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-foreground">{rx?.medication}</h3>
                    <p className="text-sm text-muted-foreground">{rx?.instructions}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Sent to {pharmacies?.find(p => p?.value === rx?.pharmacy)?.label} on {rx?.date}
                    </p>
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-success/10 text-success border border-success/20">
                    {rx?.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EPrescribing;